import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import IconCollection from '../IconCollection/IconCollection';
import { wishListListingsSelector, addToWishlist, removeFromWishlist } from '../../containers/WishListPage/WishListPage.duck';
import { IconSpinner } from '../../components';
import { createResourceLocatorString } from '../../util/routes';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { isAuthenticatedSelector } from '../../ducks/auth.duck';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  const marketplaceCurrency = config.currency;
  const price_per_day = publicData?.price_per_day;
  const formattedDayPrice = intl.formatNumber(price_per_day, {
    style: 'currency',
    currency: marketplaceCurrency,
  });

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : price ? (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      ) : (
        <span className={css.priceValue}>{formattedDayPrice ? formattedDayPrice : ''} <FormattedMessage id="ListingCard.perDay" /></span>
      )}
    </div>
  );
};

const iconHeart = <IconCollection name="outlineHeart" />
const iconHeartFilled = <IconCollection name="heartFilled" />

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const wishlistListings = useSelector(wishListListingsSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showAddress = true,
    showAddToWishlist = true,
  } = props;

  const handleAddToWishlist = () => {
    setIsLoading(true);
    dispatch(addToWishlist(listing.id.uuid)).then(() => {
      setIsLoading(false);
    });
  };

  const handleRemoveFromWishlist = () => {
    setIsLoading(true);
    dispatch(removeFromWishlist(listing.id.uuid)).then(() => {
      setIsLoading(false);
    });
  };

  const handleListingClick = () => {
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        {
          id: listing.id.uuid,
          slug: createSlug(listing.attributes.title)
        },
        {}
      )
    );
  }

  const isWishlisted = wishlistListings.find(item => item.id.uuid === listing.id.uuid);


  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const address = listing?.attributes?.publicData?.location?.address;
  const authorName = author.attributes?.profile?.displayName;
  const isFeatured = publicData?.isFeatured;
  const totalRatingSum = publicData?.totalRatingSum || 0;
  const totalRatings = publicData?.totalRatings || 0;
  const averageRating = totalRatings > 0
    ? Math.round((totalRatingSum / totalRatings) * 10) / 10
    : 0;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const fallbackVariants = firstImage && variants.length === 0
    ? Object.keys(firstImage?.attributes?.variants).filter(k => 
        k.includes('scaled-medium') || k.includes('scaled-small') || k.includes('landscape-crop')
      )
    : [];

  const finalVariants = variants.length > 0 ? variants : fallbackVariants;

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  return (
    <div className={classes}>
      <NamedLink name="ListingPage" params={{ id, slug }}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={finalVariants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>
      </NamedLink>
      {showAddToWishlist ? (
        <button
          className={css.addToWishlistButton}
          onClick={() => {
            if (!isAuthenticated) {
              history.push('/login');
              return;
            }
            isWishlisted ? handleRemoveFromWishlist() : handleAddToWishlist()
          }}
        >
          {isLoading ? (
            <IconSpinner />
          ) : isWishlisted ? (
            iconHeartFilled
          ) : (
            iconHeart
          )}
        </button>
      ) : null}
      <div className={css.info}>
        <div className={css.mainContent}>
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
          <div className={css.mainInfo}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            {showAuthorInfo ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            ) : null}
            {showAddress && address ? (
              <div className={css.addressInfo}>
                <IconCollection name="location" />
                {address}
              </div>
            ) : null}

          </div>
          <div>
            {isFeatured ? <div className={css.featuredBadge}><FormattedMessage id="ListingCard.featured" /></div> : null}

          </div>
        </div>

        <div className={css.ratingInfoContainer}>
          <div className={css.ratingInfo}>
            <IconCollection name="star" />
            <p>
              {averageRating}/5 <span>({totalRatings})</span>
            </p>
          </div>

          <button onClick={() => handleListingClick()} className={css.viewDetailsBtn}>
            <FormattedMessage id="ListingCard.viewDetails" />
          </button>

        </div>
      </div>

    </div>
  );
};

export default ListingCard;
